import { useState, useEffect, useRef } from 'react';
import { materiAPI, subjectAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Upload, FileText, Trash2, Pencil, Download, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA',
  'SMA Favorit'
];

export default function MateriTab() {
  const [materiList, setMateriList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProgram, setFilterProgram] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [subjects, setSubjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMateri, setEditingMateri] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, materi: null });
  
  const [formData, setFormData] = useState({
    title: '',
    program: '',
    subject: '',
    description: '',
    file: null
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadMateri();
  }, [filterProgram, filterSubject]);

  useEffect(() => {
    if (formData.program) {
      loadSubjects(formData.program);
    }
  }, [formData.program]);

  const loadMateri = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterProgram !== 'all') params.program = filterProgram;
      if (filterSubject !== 'all') params.subject = filterSubject;
      
      const res = await materiAPI.getAll(params);
      setMateriList(res.data);
    } catch (error) {
      toast.error('Gagal memuat data materi');
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async (program) => {
    try {
      const res = await subjectAPI.getAll(program);
      setSubjects(res.data || []);
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.program) {
      toast.error('Judul dan Program wajib diisi');
      return;
    }

    if (!editingMateri && !formData.file) {
      toast.error('File wajib dipilih');
      return;
    }

    try {
      // Normalize subject value
      const subjectValue = formData.subject === 'none' ? '' : formData.subject;
      
      if (editingMateri) {
        // Update metadata only
        await materiAPI.update(editingMateri.id, {
          title: formData.title,
          program: formData.program,
          subject: subjectValue,
          description: formData.description
        });
        toast.success('Materi berhasil diupdate');
      } else {
        // Upload new materi
        const uploadData = new FormData();
        uploadData.append('file', formData.file);
        uploadData.append('title', formData.title);
        uploadData.append('program', formData.program);
        uploadData.append('subject', subjectValue);
        uploadData.append('description', formData.description);
        
        await materiAPI.upload(uploadData);
        toast.success('Materi berhasil diupload');
      }
      
      setDialogOpen(false);
      resetForm();
      loadMateri();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan materi');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.materi) return;
    
    try {
      await materiAPI.delete(deleteDialog.materi.id);
      toast.success('Materi berhasil dihapus');
      setDeleteDialog({ open: false, materi: null });
      loadMateri();
    } catch (error) {
      toast.error('Gagal menghapus materi');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      program: '',
      subject: '',
      description: '',
      file: null
    });
    setEditingMateri(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openEditDialog = (materi) => {
    setEditingMateri(materi);
    setFormData({
      title: materi.title,
      program: materi.program,
      subject: materi.subject,
      description: materi.description,
      file: null
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Upload Materi</h1>
        <Button onClick={openCreateDialog} className="bg-[#4472C4] hover:bg-[#3461B3]">
          <Upload className="w-4 h-4 mr-2" />
          Tambah Materi
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Label className="text-sm text-slate-500">Jenjang</Label>
              <Select value={filterProgram} onValueChange={setFilterProgram}>
                <SelectTrigger>
                  <SelectValue placeholder="Semua Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Program</SelectItem>
                  {PROGRAMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Materi List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daftar Materi ({materiList.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-slate-500">Memuat...</p>
            </div>
          ) : materiList.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Belum ada materi</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#4472C4] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left">No</th>
                    <th className="px-4 py-3 text-left">Judul Materi</th>
                    <th className="px-4 py-3 text-left">Tanggal Upload</th>
                    <th className="px-4 py-3 text-left">Jenjang</th>
                    <th className="px-4 py-3 text-left">Pelajaran</th>
                    <th className="px-4 py-3 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {materiList.map((materi, idx) => (
                    <tr key={materi.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium">{materi.title}</td>
                      <td className="px-4 py-3 text-slate-500">
                        {new Date(materi.created_at).toLocaleDateString('id-ID')}
                      </td>
                      <td className="px-4 py-3">{materi.program}</td>
                      <td className="px-4 py-3">{materi.subject || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`${BACKEND_URL}${materi.file_url}`, '_blank')}
                            className="bg-blue-500 text-white hover:bg-blue-600"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(materi)}
                            className="bg-[#4472C4] text-white hover:bg-[#3461B3]"
                          >
                            Ubah
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteDialog({ open: true, materi })}
                          >
                            Hapus
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMateri ? 'Edit Materi' : 'Tambah Materi'}</DialogTitle>
            <DialogDescription>
              {editingMateri ? 'Update informasi materi' : 'Upload materi pembelajaran baru (PDF)'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Judul Materi *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Judul materi"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Jenjang Dipilih *</Label>
                <Select
                  value={formData.program}
                  onValueChange={(v) => setFormData({ ...formData, program: v, subject: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Jenjang" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAMS.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Pelajaran Dipilih</Label>
                <Select
                  value={formData.subject}
                  onValueChange={(v) => setFormData({ ...formData, subject: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih pelajaran" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ada</SelectItem>
                    {subjects.map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!editingMateri && (
              <div>
                <Label>Upload Materi *</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.csv"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  required={!editingMateri}
                />
                <p className="text-xs text-slate-500 mt-1">*file ext: .pdf, .doc, .docx, .csv</p>
              </div>
            )}

            <div>
              <Label>Deskripsi</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Deskripsi materi (opsional)"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Batal
              </Button>
              <Button type="submit" className="bg-[#4472C4] hover:bg-[#3461B3]">
                {editingMateri ? 'Update' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, materi: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Materi?</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus materi "{deleteDialog.materi?.title}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
